import React, { Component } from 'react';
import HttpRunner from '../query';
import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';
import miscUtils from '../../../utils/miscUtils';

class DocMeasurementEntity extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSample: false
        }
    }

    componentDidMount() {
        this.setState({
            dynamicSample: perms.checkEndpoint(urlHandler.API_ENDPOINT_V2_ENRICH)
        }) 
    }

    render() {
        return (
            <div className="bucket" id="top">
                <div>
                    <h3 id="measurement" className="heading-1">Measurement</h3>
                    <div className="description">
                        The software can extract various measurement types. This can be an exact measurement or a range of 
                        measurements. All measurement extractions will have a normalized value or range of normalized values 
                        based on a metric prefix (if extracted). The entity label will then represent this normalized 
                        value (or range) and the base units.<br/>
                        <br/>
                        The current implemented measurement types and their corresponding base units are:
                        <div className="example-content">
                            <span className="line">Frequency (Hz)</span>
                            <span className="line">Nautical Speed (Knots)</span>
                            <span className="line">Percent (%, percent)</span>
                            <span className="line">Speed (mph, m/s)</span>
                            <span className="line">Velocity (mph NW)</span>
                        </div>
                        <br/>
                        An extracted measurement may be represented by the following attributes:
                    </div>

                    <table className="table-wrapper">
                        <thead className="table-header">
                        <tr>
                            <th>Attribute </th>
                            <th>Meaning </th>
                            <th>Type </th>
                            <th>Constraints </th>
                            <th>Notes </th>
                        </tr>
                        </thead>
                        <tbody className="table-body">
                        <tr>
                            <td className="attribute">direction</td>
                            <td className="meaning">The direction if the measurement type is a vector</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">A normalized short form value if non-angular (E, N, NE, NW, S, SE, SW, W)<br/>
                                <br/>
                                May be expressed in degrees if angular.<br/>
                                <br/>
                                Only applies to measurement type <i>velocity</i>.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">normalizedValue</td>
                            <td className="meaning">The normalized value</td>
                            <td className="type">Number</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">A normalized value based on a metric prefix (if extracted) and the units.<br/>
                                <br/>
                                May be expressed in scientific notation if the value is very large or very small, ex. 3E+12.<br/>
                                <br/>
                                This attribute will always be represented if an exact measurement and not a range.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">startValue</td>
                            <td className="meaning">The normalized start value</td>
                            <td className="type">Number</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">A normalized value based on a metric prefix (if extracted) and the units.<br/>
                                <br/>
                                May be expressed in scientific notation if the value is very large or very small, ex. 3E+12<br/>
                                <br/>
                                This attribute will always be represented if the extraction is a range of measurements.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">endValue</td>
                            <td className="meaning">The normalized end value</td>
                            <td className="type">Number</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">A normalized value based on a metric prefix (if extracted) and the units.<br/>
                                <br/>
                                May be expressed in scientific notation if the value is very large or very small, ex. 3E+12<br/>
                                <br/>
                                This attribute will always be represented if the extraction is a range of measurements.
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">type</td>
                            <td className="meaning">The measurement type</td>
                            <td className="type">String</td>
                            <td className="constraints">Mandatory</td>
                            <td className="notes">The type of measurement based on the extracted units.</td>
                        </tr>
                        <tr>
                            <td className="attribute">units</td>
                            <td className="meaning">The base units for the measurement type</td>
                            <td className="type">String</td>
                            <td className="constraints">Mandatory</td>
                            <td className="notes">Will always be represented in base units with no prefix.</td>

                        </tr>
                        </tbody>
                    </table>

                    <h4 className="heading-3">Frequency Enrichment</h4>
                    <div className="description">Some additional attributes may accompany MEASUREMENT extractions with a measurement 
                        type of frequency. These enriched attributes may include the radio band designation standards for the extracted 
                        frequency value. More information on these standards can be 
                        found at {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/Radio_spectrum#Comparison_of_radio_band_designation_standards')}.
                    </div>
                    <table className="table-wrapper">
                        <thead className="table-header">
                        <tr>
                            <th>Attribute </th>
                            <th>Meaning </th>
                            <th>Type </th>
                            <th>Constraints </th>
                            <th>Notes </th>
                        </tr>
                        </thead>
                        <tbody className="table-body">
                        <tr>
                            <td className="attribute">ieeeBand</td>
                            <td className="meaning">The IEEE radio band</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">The band abbreviation
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">ituBand</td>
                            <td className="meaning">The ITU radar band</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">The band abbreviation
                            </td>
                        </tr>
                        <tr>
                            <td className="attribute">natoBand</td>
                            <td className="meaning">The NATO (EU or US ECM) frequency designations</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">The band abbreviation
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <h4 className="heading-2">Examples</h4>
                    <div className="example-content">The chip is the first product to implement the 
                        full ITU-T G.hn specification with MIMO support and up to 80 MHz.</div>
                    <HttpRunner example="measurement1" 
                        query="enrich"
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_MEASURE1) : null}
                    />
                    <br/>
                    <div className="example-content">The radio spectrum is the part of the 
                        electromagnetic spectrum with frequencies from 3 Hz to 3 THz.</div>
                    <HttpRunner example="measurement2" 
                        query="enrich"
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_MEASURE2) : null}
                    />
                    <br/>
                    <div className="example-content">In terms of data, the euro zone registered a 
                        growth rate 0.2 percent on the quarter and of 1.2 percent on the year at the end of 2018.</div>
                    <HttpRunner example="measurement3" 
                        query="enrich"
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_MEASURE3) : null}
                    />
                </div>
            </div>
        );
    }
}

export default DocMeasurementEntity;